import postal from 'postal';
import { roadblockMessageName } from 'utils/roadblocks';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { useEffect } from 'react';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { JourneyEvent } from 'branch-sdk';
import User from '@alltrails/shared/types/User';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';

type Props = {
  branchKey: string;
  mobileBrowser: boolean;
};

function handleAfterRoadblock(afterRoadblock: (() => void) | string, openInNewTab: boolean) {
  if (typeof afterRoadblock === 'function') {
    afterRoadblock();
  } else if (openInNewTab) {
    window.open(afterRoadblock, '_blank');
  } else {
    window.location.assign(afterRoadblock);
  }
}

function handleRoadblock({
  afterRoadblock,
  atRef,
  eventName,
  languageRegionCode,
  mobileBrowser,
  openInNewTab,
  returnToUrl,
  user
}: {
  afterRoadblock: (() => void) | string;
  atRef?: string;
  eventName: string;
  languageRegionCode: LanguageRegionCode;
  mobileBrowser: boolean;
  openInNewTab?: boolean;
  returnToUrl: string;
  user: User;
}) {
  if (!user) {
    const ref = atRef ? `ref=${atRef}&` : '';
    const returnTo = `returnTo=${encodeURIComponent(returnToUrl)}`;
    window.location.assign(LanguageSupportUtil.wrapUrlSafe(`/signup?${ref}${returnTo}`, languageRegionCode));
    return;
  }

  // Proceed directly to next step if not mobile
  if (!mobileBrowser) {
    handleAfterRoadblock(afterRoadblock, openInNewTab);
    return;
  }

  // Only a subset of roadblocks apply to signed-in-user
  const signedInRoadblocks = ['trail_header_map', 'trail_explore', 'trail_print', 'park_print', 'create_map', 'print_maps'];

  // Show only every third time
  const signedInRoadblockEvery = 3;
  if (window && window.localStorage && signedInRoadblocks.includes(eventName)) {
    const authRoadblockCount = (Number(window.localStorage.getItem('authRoadblockCount')) + 1) % signedInRoadblockEvery;
    window.localStorage.setItem('authRoadblockCount', String(authRoadblockCount));

    if (authRoadblockCount + 1 === signedInRoadblockEvery) {
      global.branch.addListener(null, (event: JourneyEvent) => {
        if (event === 'willNotShowJourney' || event === 'didCloseJourney') {
          handleAfterRoadblock(afterRoadblock, openInNewTab);
        }
      });

      global.branch.track('pageview', {
        AUTHENTICATED: 'true',
        SHOW_JOURNEY: 'MOBILE_ROADBLOCK'
      });

      return;
    }
  }

  // No Journey shown, so proceed directly to next step
  handleAfterRoadblock(afterRoadblock, openInNewTab);
}

/*
  Embed an instance of this component on a page that has mobile-roadblocked links or modals.
  Branch will be watching for us pushing the metadata that triggers a Journey show.
*/
export default function MobileRoadblock({ branchKey, mobileBrowser }: Props): any {
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    if (mobileBrowser && global.branch) {
      const branchOptions = {
        disable_entry_animation: true,
        disable_exit_animation: true
      };

      global.branch.init(branchKey, branchOptions);
    }

    const messagingChannel = postal.channel('explore_messagingChannel');
    const listener = messagingChannel.subscribe(roadblockMessageName, (data: any) =>
      handleRoadblock({
        afterRoadblock: data.afterRoadblock,
        atRef: data.atRef,
        eventName: data.event,
        languageRegionCode,
        mobileBrowser,
        openInNewTab: data.openInNewTab,
        returnToUrl: data.returnToUrl,
        user: data.currentUser
      })
    );

    return () => {
      if (listener) {
        postal.unsubscribe(listener);
      }
    };
  }, [branchKey, languageRegionCode, mobileBrowser]);

  return null;
}

export { handleRoadblock };
